import {ArchivedTask, BoardState, BoardMember, BoardStats, BoardTask, KioskPhoto, SeriesScope, TaskSearchResult} from "../types/task";
import {callApi} from "./api";
import * as contract from "../contract";


// Each function is one call described in the API contract (src/contract): the address, the request and the shape of
// the response are checked against it, and a failure throws, so a caller never mistakes a failed request for an
// empty result (the app's global handler logs it).

export const getBoardsQuery = async(): Promise<{ boards: contract.BoardSummary[], kioskBoardId: string | null }> => {
    const { boards, kioskBoardId } = await callApi(contract.boards.getBoards);

    return { boards, kioskBoardId };
}

export const getBoardQuery = async(boardId: string): Promise<BoardState> => {
    const { board, archiveAfterDays, viewerRole, isKiosk } = await callApi(contract.boards.getBoard, { params: { boardId } });

    return {
        id: board.id,
        sectionOrder: board.sectionOrder ?? [],
        sectionsById: Object.fromEntries(
            Object.entries(board.sections ?? {}).map(([sectionId, section]) => [sectionId, { id: section.id, title: section.title, taskIds: section.taskIds ?? [] }]),
        ),
        title: board.title,
        kioskMessage: board.kioskMessage,
        kioskPhotoUrls: board.kioskPhotoUrls ?? [],
        archiveAfterDays,
        viewerRole,
        isKiosk,
    };
}

export const createBoardQuery = async(boardTitle: string): Promise<string> => {
    const { boardId } = await callApi(contract.boards.createBoard, { body: { boardTitle } });

    return boardId;
}

export const createSectionQuery = async(boardId: string, sectionTitle: string): Promise<string> => {
    const { sectionId } = await callApi(contract.boards.createSection, { body: { boardId, sectionTitle } });

    return sectionId;
}

export const getSectionTasksQuery = async(boardId: string): Promise<BoardTask[]> => {
    const { sectionTasks } = await callApi(contract.tasks.getSectionTasks, { params: { boardId } });

    return sectionTasks;
}

export const createSectionTaskQuery = async(boardId: string, sectionId: string, taskTitle: string, taskDescription: string, taskDate?: string): Promise<string> => {
    const { taskId } = await callApi(contract.tasks.createSectionTask, { body: { boardId, sectionId, taskTitle, taskDescription, taskDate } });

    return taskId;
}

// Creates one task per date, all in one recurring series. Throws on failure so the
// caller can show the server's message.
export const createRecurringTasksQuery = async(boardId: string, sectionId: string, taskTitle: string, dates: string[], taskDescription = '') =>
    callApi(contract.tasks.createRecurringTasks, { body: { boardId, sectionId, taskTitle, taskDescription, dates } });

// Full-text search over task titles on a board, best match first. A recurring series comes
// back once, as its occurrence nearest to today.
export const searchTasksQuery = async(boardId: string, query: string): Promise<TaskSearchResult[]> => {
    const { results } = await callApi(contract.tasks.searchTasks, { params: { boardId }, query: { q: query } });

    return results;
}

// Completion statistics for [from, to); `timeZone` decides where the days in perDay split.
export const getBoardStatsQuery = async(boardId: string, from: string, to: string, timeZone: string): Promise<BoardStats> => {
    const { stats } = await callApi(contract.stats.getBoardStats, { params: { boardId }, query: { from, to, tz: timeZone } });

    return stats;
}

// Archive or restore one task. Throws on failure so callers can react.
export const setTaskArchivedQuery = async(taskId: string, archived: boolean) => {
    await callApi(contract.tasks.setTaskArchived, { body: { taskId, archived } });
}

export const getArchivedTasksQuery = async(boardId: string): Promise<ArchivedTask[]> => {
    const { archivedTasks } = await callApi(contract.tasks.getArchivedTasks, { params: { boardId } });

    return archivedTasks;
}

export const updateArchiveSettingsQuery = async(boardId: string, archiveAfterDays: number) => {
    await callApi(contract.boards.updateArchiveSettings, { body: { boardId, archiveAfterDays } });
}

export const updateTaskSeriesQuery = async(taskId: string, scope: SeriesScope, updates: { title?: string, description?: string, sectionId?: string, shiftMinutes?: number }) =>
    callApi(contract.tasks.updateTaskSeries, { body: { taskId, scope, ...updates } });

export const deleteTaskSeriesQuery = async(taskId: string, scope: SeriesScope) =>
    callApi(contract.tasks.deleteTaskSeries, { body: { taskId, scope } });

export const deleteSectionTaskQuery = async(boardId: string, sectionId: string, taskId: string): Promise<string> => {
    const reply = await callApi(contract.tasks.deleteSectionTask, { body: { boardId, sectionId, taskId } });

    return reply.taskId;
}

export const deleteSectionQuery = async(boardId: string, sectionId: string): Promise<string> => {
    const reply = await callApi(contract.boards.deleteSection, { body: { boardId, sectionId } });

    return reply.sectionId;
}

export const updateSectionTitleQuery = async(boardId: string, sectionId: string, sectionTitle: string): Promise<string> => {
    const reply = await callApi(contract.boards.updateSectionTitle, { body: { boardId, sectionId, sectionTitle } });

    return reply.sectionId;
}

export const updateSectionTaskQuery = async(taskId: string, updatedAttributes: contract.TaskChanges): Promise<string> => {
    const reply = await callApi(contract.tasks.updateSectionTask, { body: { taskId, updatedAttributes } });

    return reply.taskId;
}

export const updateBoardTitleQuery = async(boardId: string, boardTitle: string): Promise<string> => {
    const reply = await callApi(contract.boards.updateBoardTitle, { body: { boardId, boardTitle } });

    return reply.boardId;
}

export const updateKioskMessageQuery = async(boardId: string, kioskMessage: string) =>
    callApi(contract.kiosk.updateKioskMessage, { body: { boardId, kioskMessage } });

export const updateKioskPhotosQuery = async(boardId: string, photoUrls: string[]) =>
    callApi(contract.kiosk.updateKioskPhotos, { body: { boardId, photoUrls } });

// Photos go browser -> S3 directly via a presigned URL; the API only issues the
// URL and then records the finished upload.
export const uploadKioskPhotoQuery = async(boardId: string, file: File): Promise<string> => {
    const { photoId, uploadUrl } = await callApi(contract.kiosk.createKioskPhotoUploadUrl, {
        params: { boardId },
        body: { contentType: file.type as contract.KioskPhotoType, size: file.size },
    });

    // Plain fetch rather than the axios proxy: S3 rejects a request that carries
    // our Authorization header on top of the presigned signature.
    const putResponse = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {'Content-Type': file.type},
        body: file,
    });
    if (!putResponse.ok) {
        throw new Error('Upload to photo storage failed');
    }

    await callApi(contract.kiosk.registerKioskPhoto, { params: { boardId }, body: { photoId } });

    return photoId;
}

export const getKioskPhotosQuery = async(boardId: string): Promise<KioskPhoto[]> => {
    const { photos } = await callApi(contract.kiosk.getKioskPhotos, { params: { boardId } });

    return photos;
}

export const deleteKioskPhotoQuery = async(photoId: string) =>
    callApi(contract.kiosk.deleteKioskPhoto, { body: { photoId } });

export const updateMemberRoleQuery = async(boardId: string, userId: string, role: contract.MemberRole, kiosk?: boolean) =>
    callApi(contract.members.updateMemberRole, { body: { boardId, userId, role, kiosk } });

export const getMembers = async(boardId: string): Promise<BoardMember[]> => {
    const { members } = await callApi(contract.members.getBoardMembers, { params: { boardId } });

    return members;
}

// Google Calendars connected to a board (see the API contract, calendar.ts). Connecting reads the calendar at once and throws, with the server's
// message, if the address does not work.
export const getCalendarFeedsQuery = async(boardId: string) =>
    callApi(contract.calendar.getCalendarFeeds, { params: { boardId } });

export const connectCalendarFeedQuery = async(boardId: string, url: string, sectionId: string, timeZone: string) =>
    callApi(contract.calendar.connectCalendarFeed, { params: { boardId }, body: { url, sectionId, timeZone } });

export const syncCalendarFeedQuery = async(feedId: string) =>
    callApi(contract.calendar.syncCalendarFeed, { body: { feedId } });

export const disconnectCalendarFeedQuery = async(feedId: string, deleteTasks: boolean) =>
    callApi(contract.calendar.disconnectCalendarFeed, { body: { feedId, deleteTasks } });
